/**
 * ComplianceCalendar — Indian Statutory Compliance Deadlines
 * Shows PF, ESI, TDS, PT, LWF, Income Tax, Labour Law deadlines
 */
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Badge, Button, Alert } from 'react-bootstrap';
import { ClipboardList, ChevronLeft, ChevronRight, Bell, Download } from 'lucide-react';
import { Spinner } from 'react-bootstrap';
import axiosInstance from '../../api/axiosConfig';

const CATEGORY_COLOURS = {
    'PF':           { bg:'#dbeafe', text:'#1e40af', border:'#3b82f6' },
    'ESI':          { bg:'#d1fae5', text:'#065f46', border:'#10b981' },
    'TDS/Income Tax':{ bg:'#fef3c7', text:'#92400e', border:'#f59e0b' },
    'PT':           { bg:'#ede9fe', text:'#5b21b6', border:'#8b5cf6' },
    'LWF':          { bg:'#fce7f3', text:'#9d174d', border:'#ec4899' },
    'Labour Law':   { bg:'#ffedd5', text:'#9a3412', border:'#f97316' },
    'General':      { bg:'#f1f5f9', text:'#475569', border:'#94a3b8' },
};
const PRIORITY_CONFIG = {
    critical: { bg:'danger',  label:'Critical' },
    high:     { bg:'warning', label:'High'     },
    medium:   { bg:'info',    label:'Medium'   },
};
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const ComplianceCalendar = () => {
    const [year,    setYear]    = useState(new Date().getFullYear());
    const [data,    setData]    = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter,  setFilter]  = useState('All');
    const [upcoming, setUpcoming] = useState([]);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            axiosInstance.get('/compliance/calendar', { params: { year } }),
            axiosInstance.get('/compliance/upcoming', { params: { days: 30 } }),
        ]).then(([cal, up]) => {
            setData(cal.data.data);
            setUpcoming(up.data.data?.events || []);
        }).catch(() => {}).finally(() => setLoading(false));
    }, [year]);

    const categories = ['All', 'PF', 'ESI', 'TDS/Income Tax', 'PT', 'Labour Law', 'LWF', 'General'];

    const filterEvents = (events) =>
        filter === 'All' ? events : events.filter(e => e.category === filter);

    const todayStr = new Date().toISOString().slice(0, 10);
    const isPast   = (date) => date < todayStr;
    const isToday  = (date) => date === todayStr;
    const isDue    = (date) => {
        const d = new Date(date);
        const t = new Date();
        const diff = (d - t) / (1000 * 60 * 60 * 24);
        return diff >= 0 && diff <= 7;
    };

    return (
        <div className="p-2 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1">
                        <ClipboardList size={22} className="me-2 text-primary"/>
                        Compliance Calendar
                    </h4>
                    <p className="text-muted small mb-0">
                        Indian statutory deadlines — PF · ESI · TDS · PT · LWF · Income Tax
                    </p>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <Button size="sm" variant="outline-secondary" onClick={() => setYear(y => y-1)}>
                        <ChevronLeft size={16}/>
                    </Button>
                    <span className="fw-bold">{year}</span>
                    <Button size="sm" variant="outline-secondary" onClick={() => setYear(y => y+1)}>
                        <ChevronRight size={16}/>
                    </Button>
                </div>
            </div>

            {/* Upcoming deadlines alert */}
            {upcoming.length > 0 && (
                <Alert variant="warning" className="d-flex align-items-start gap-2 mb-4">
                    <Bell size={18} className="flex-shrink-0 mt-1"/>
                    <div>
                        <strong>{upcoming.length} compliance deadline{upcoming.length > 1 ? 's' : ''} in the next 30 days:</strong>
                        <div className="mt-1 d-flex flex-wrap gap-2">
                            {upcoming.slice(0, 5).map((e, i) => (
                                <Badge key={i} bg="warning" text="dark" className="fw-normal">
                                    {e.title} — {new Date(e.date).toLocaleDateString('en-IN', { day:'numeric', month:'short' })}
                                </Badge>
                            ))}
                            {upcoming.length > 5 && <Badge bg="secondary">+{upcoming.length - 5} more</Badge>}
                        </div>
                    </div>
                </Alert>
            )}

            {/* Category filter */}
            <div className="d-flex gap-1 flex-wrap mb-4">
                {categories.map(cat => (
                    <Button key={cat} size="sm"
                        variant={filter === cat ? 'primary' : 'outline-secondary'}
                        onClick={() => setFilter(cat)}
                        style={{ fontSize:12 }}>
                        {cat}
                    </Button>
                ))}
            </div>

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>
            ) : (
                <div>
                    {Object.entries(data?.by_month || {}).map(([monthKey, events]) => {
                        const filtered = filterEvents(events);
                        if (!filtered.length) return null;
                        const [y, m] = monthKey.split('-').map(Number);
                        const monthName = MONTHS[m - 1];

                        return (
                            <Card key={monthKey} className="border-0 shadow-sm mb-4">
                                <Card.Header className="bg-white border-0 pt-3 pb-2">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <h6 className="fw-bold mb-0">{monthName} {y}</h6>
                                        <Badge bg="light" text="dark">{filtered.length} deadline{filtered.length > 1 ? 's' : ''}</Badge>
                                    </div>
                                </Card.Header>
                                <Card.Body className="pt-0 pb-3 px-4">
                                    <div className="d-flex flex-column gap-2">
                                        {filtered.map((e, i) => {
                                            const colours = CATEGORY_COLOURS[e.category] || CATEGORY_COLOURS.General;
                                            const pc      = PRIORITY_CONFIG[e.priority] || PRIORITY_CONFIG.medium;
                                            const past    = isPast(e.date);
                                            const today   = isToday(e.date);
                                            const due     = isDue(e.date);
                                            const day     = parseInt(e.date.slice(8));

                                            return (
                                                <div key={i} style={{
                                                    display:'flex', gap:16, alignItems:'flex-start',
                                                    padding:'12px 16px', borderRadius:10,
                                                    background: today ? '#fef3c7' : due ? '#fff7ed' : past ? '#f8fafc' : '#fff',
                                                    border: `1px solid ${today ? '#f59e0b' : due ? '#fed7aa' : colours.border}30`,
                                                    opacity: past && !today ? 0.65 : 1,
                                                }}>
                                                    {/* Day circle */}
                                                    <div style={{
                                                        width:44, height:44, borderRadius:10, flexShrink:0,
                                                        background: past ? '#f1f5f9' : colours.bg,
                                                        color: past ? '#94a3b8' : colours.text,
                                                        display:'flex', flexDirection:'column',
                                                        alignItems:'center', justifyContent:'center',
                                                        fontSize:11, fontWeight:700,
                                                    }}>
                                                        <span style={{ fontSize:18, lineHeight:1 }}>{day}</span>
                                                        <span style={{ fontSize:9 }}>{monthName.slice(0,3)}</span>
                                                    </div>
                                                    {/* Details */}
                                                    <div style={{ flex:1 }}>
                                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                                            <span className="fw-bold small">{e.title}</span>
                                                            <Badge style={{ fontSize:9, background:colours.bg, color:colours.text }}>
                                                                {e.category}
                                                            </Badge>
                                                            <Badge bg={pc.bg} style={{ fontSize:9 }}>
                                                                {pc.label}
                                                            </Badge>
                                                            {today && <Badge bg="warning" text="dark" style={{ fontSize:9 }}>TODAY</Badge>}
                                                            {due && !today && <Badge bg="danger" style={{ fontSize:9 }}>DUE SOON</Badge>}
                                                            {past && !today && <Badge bg="secondary" style={{ fontSize:9 }}>DONE</Badge>}
                                                        </div>
                                                        <div className="text-muted mt-1" style={{ fontSize:12 }}>
                                                            {e.description}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Card.Body>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Legend */}
            <Card className="border-0 shadow-sm mt-4 p-4">
                <div className="fw-bold small mb-3">Category Legend</div>
                <div className="d-flex flex-wrap gap-2">
                    {Object.entries(CATEGORY_COLOURS).map(([cat, c]) => (
                        <div key={cat} style={{
                            background: c.bg, color: c.text,
                            padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:600,
                        }}>
                            {cat}
                        </div>
                    ))}
                </div>
                <p className="text-muted small mt-3 mb-0">
                    ⚠️ Dates are standard Indian deadlines. PT dates vary by state — verify with your CA.
                    TDS dates may shift if they fall on a holiday/weekend.
                </p>
            </Card>
        </div>
    );
};

export default ComplianceCalendar;
